/**
 * @copyright 2024-2026 David Baugh
 * @license Proprietary - All Rights Reserved
 * Unauthorized copying, modification, or distribution is strictly prohibited.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { useToast } from '../ui/use-toast';
import { 
  Search,
  Upload,
  Trash2,
  Edit3,
  Eye,
  Tag,
  FileImage,
  X,
  ChevronLeft,
  ChevronRight,
  MoreVertical
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';

const ImageGallery = ({ onImageSelect, selectMode = false }) => {
  const [images, setImages] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    tag: '',
    creator: '',
    usage: '',
    sortBy: 'created_at',
    sortOrder: 'DESC'
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    total: 0,
    totalPages: 0
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const [editingImage, setEditingImage] = useState(null);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showImageDetails, setShowImageDetails] = useState(false);
  const [uploadData, setUploadData] = useState({
    originalName: '',
    description: '',
    altText: '',
    tags: []
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [newTag, setNewTag] = useState('');

  const { toast } = useToast();

  // Fetch images from API
  const fetchImages = useCallback(async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      });

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/images?${queryParams}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setImages(data.images);
        setPagination(prev => ({
          ...prev,
          total: data.pagination.total,
          totalPages: data.pagination.totalPages
        }));
      } else {
        throw new Error('Failed to fetch images');
      }
    } catch (error) {
      console.error('Error fetching images:', error);
      toast({
        title: "Error",
        description: "Failed to fetch images",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.limit, toast]);

  // Fetch available tags
  const fetchTags = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/image-tags`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setTags(data);
      }
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  }, []);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  // Upload new image
  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', selectedFile);
      formData.append('originalName', uploadData.originalName || selectedFile.name);
      formData.append('description', uploadData.description);
      formData.append('altText', uploadData.altText);
      uploadData.tags.forEach(tag => formData.append('tags[]', tag));

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/images`, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      if (response.ok) {
        await response.json();
        toast({
          title: "Success",
          description: "Image uploaded successfully"
        });
        setShowUploadDialog(false);
        setUploadData({ originalName: '', description: '', altText: '', tags: [] });
        setSelectedFile(null);
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
          setPreviewUrl(null);
        }
        fetchImages();
        fetchTags(); // Refresh tags in case new ones were created
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: "Error",
        description: "Failed to upload image",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  // Update image metadata
  const handleUpdateImage = async (imageId, updateData) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/images/${imageId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData),
        credentials: 'include'
      });

      if (response.ok) {
        toast({
          title: "Success",
          description: "Image updated successfully"
        });
        setEditingImage(null);
        fetchImages();
        fetchTags();
      } else {
        throw new Error('Update failed');
      }
    } catch (error) {
      console.error('Update error:', error);
      toast({
        title: "Error",
        description: "Failed to update image",
        variant: "destructive"
      });
    }
  };

  // Delete image
  const handleDeleteImage = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/images/${imageId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast({
          title: "Success",
          description: "Image deleted successfully"
        });
        fetchImages();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Delete failed');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete image",
        variant: "destructive"
      });
    }
  };

  // Add new tag to upload data
  const addTag = () => {
    if (newTag.trim() && !uploadData.tags.includes(newTag.trim())) {
      setUploadData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  // Remove tag from upload data
  const removeTag = (tagToRemove) => {
    setUploadData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    // Convert "all" back to empty string for filters
    let actualValue = value;
    if ((key === 'tag' || key === 'usage') && value === 'all') {
      actualValue = '';
    }
    setFilters(prev => ({ ...prev, [key]: actualValue }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Image Gallery</h2>
          <p className="text-gray-600">Manage and organize your test images</p>
        </div>
        {!selectMode && (
          <>
            <Button 
              className="bg-blue-600 hover:bg-blue-700"
              onClick={() => setShowUploadDialog(true)}
            >
              <Upload className="h-4 w-4 mr-2" />
              Upload Image
            </Button>
          
            {/* Upload Dialog */}
            <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Upload New Image</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="image-file">Select Image</Label>
                  <Input
                    id="image-file"
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setSelectedFile(file);
                        setUploadData(prev => ({ ...prev, originalName: file.name }));
                        // Create preview URL
                        if (previewUrl) {
                          URL.revokeObjectURL(previewUrl);
                        }
                        setPreviewUrl(URL.createObjectURL(file));
                      }
                    }}
                  />
                </div>
                {selectedFile && (
                  <div>
                    <Label htmlFor="filename">Filename</Label>
                    <Input
                      id="filename"
                      value={uploadData.originalName}
                      onChange={(e) => setUploadData(prev => ({ ...prev, originalName: e.target.value }))}
                      placeholder="Enter filename..."
                    />
                  </div>
                )}
                {previewUrl && (
                  <div>
                    <Label>Preview</Label>
                    <img src={previewUrl} alt="Preview" className="max-w-full h-32 object-contain border rounded" />
                  </div>
                )}
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={uploadData.description}
                    onChange={(e) => setUploadData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Brief description of the image..."
                  />
                </div>
                <div>
                  <Label htmlFor="alt-text">Alt Text</Label>
                  <Input
                    id="alt-text"
                    value={uploadData.altText}
                    onChange={(e) => setUploadData(prev => ({ ...prev, altText: e.target.value }))}
                    placeholder="Alternative text for accessibility..."
                  />
                </div>
                <div>
                  <Label>Tags</Label>
                  <div className="flex space-x-2 mb-2">
                    <Input
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      placeholder="Add a tag..."
                      onKeyPress={(e) => e.key === 'Enter' && addTag()}
                    />
                    <Button type="button" onClick={addTag} size="sm">
                      <Tag className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {uploadData.tags.map((tag, index) => (
                      <Badge key={index} variant="secondary" className="cursor-pointer" onClick={() => removeTag(tag)}>
                        {tag} <X className="h-3 w-3 ml-1" />
                      </Badge>
                    ))}
                  </div>
                </div>
                
                <div className="flex justify-end space-x-2 pt-4">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => {
                      setShowUploadDialog(false);
                      setSelectedFile(null);
                      setUploadData({ originalName: '', description: '', altText: '', tags: [] });
                      if (previewUrl) {
                        URL.revokeObjectURL(previewUrl);
                        setPreviewUrl(null);
                      }
                    }}
                    disabled={uploading}
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="button" 
                    onClick={handleUpload}
                    disabled={uploading || !selectedFile}
                  >
                    {uploading ? (
                      <>
                        <Upload className="h-4 w-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Upload Image
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </DialogContent>
            </Dialog>
          </>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <Label htmlFor="search">Search</Label>
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
                <Input
                  id="search"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  placeholder="Search images..."
                  className="pl-10"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="tag-filter">Filter by Tag</Label>
              <Select value={filters.tag || 'all'} onValueChange={(value) => handleFilterChange('tag', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="All tags" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All tags</SelectItem>
                  {tags.map(tag => (
                    <SelectItem key={tag.id} value={tag.name}>
                      <div className="flex items-center space-x-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: tag.color }}
                        />
                        <span>{tag.name} ({tag.usage_count})</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="usage-filter">Usage Status</Label>
              <Select value={filters.usage || 'all'} onValueChange={(value) => handleFilterChange('usage', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="All images" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Images</SelectItem>
                  <SelectItem value="used">In Use</SelectItem>
                  <SelectItem value="unused">Not In Use</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="sort-by">Sort By</Label>
              <Select value={filters.sortBy} onValueChange={(value) => handleFilterChange('sortBy', value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="created_at">Date Added</SelectItem>
                  <SelectItem value="original_name">Name</SelectItem>
                  <SelectItem value="file_size">Size</SelectItem>
                  <SelectItem value="updated_at">Last Modified</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="sort-order">Order</Label>
              <Select value={filters.sortOrder} onValueChange={(value) => handleFilterChange('sortOrder', value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="DESC">Newest First</SelectItem>
                  <SelectItem value="ASC">Oldest First</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Image Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {loading ? (
          Array.from({ length: 8 }).map((_, index) => (
            <Card key={index} className="animate-pulse">
              <div className="aspect-square bg-gray-200 rounded-t-lg" />
              <CardContent className="p-4">
                <div className="h-4 bg-gray-200 rounded mb-2" />
                <div className="h-3 bg-gray-200 rounded w-3/4" />
              </CardContent>
            </Card>
          ))
        ) : images.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <FileImage className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Images Found</h3>
            <p className="text-gray-600 mb-4">Upload your first image to get started</p>
            {!selectMode && (
              <Button onClick={() => setShowUploadDialog(true)}>
                <Upload className="h-4 w-4 mr-2" />
                Upload Image
              </Button>
            )}
          </div>
        ) : (
          images.map((image) => (
            <Card key={image.id} className="overflow-hidden hover:shadow-lg transition-shadow">
              <div className="aspect-square relative">
                <img
                  src={`${process.env.REACT_APP_API_URL}${image.file_path}`}
                  alt={image.alt_text || image.original_name}
                  className="w-full h-full object-cover cursor-pointer"
                  onClick={() => {
                    if (selectMode) {
                      onImageSelect(image);
                    } else {
                      setSelectedImage(image);
                      setShowImageDetails(true);
                    }
                  }}
                />
                {!selectMode && (
                  <div className="absolute top-2 right-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="secondary" size="sm" className="h-8 w-8 p-0">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => {
                            setSelectedImage(image);
                            setShowImageDetails(true);
                          }}
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          View Details
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => setEditingImage(image)}
                        >
                          <Edit3 className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleDeleteImage(image.id)}
                          className="text-red-600"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                )}
                {/* Usage indicator badge */}
                <div className="absolute top-2 left-2">
                  {image.usage_count > 0 ? (
                    <Badge variant="default" className="bg-green-600 text-white text-xs">
                      In Use ({image.usage_count})
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="bg-gray-500 text-white text-xs">
                      Unused
                    </Badge>
                  )}
                </div>
              </div>
              <CardContent className="p-3">
                <h3 className="font-medium text-sm mb-1 truncate" title={image.original_name}>
                  {image.original_name}
                </h3>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>{formatFileSize(image.file_size)}</span>
                  <span>{image.usage_count} uses</span>
                </div>
                {image.tags && image.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {image.tags.slice(0, 2).map((tag, index) => (
                      <Badge key={index} variant="outline" className="text-xs">
                        {tag.name}
                      </Badge>
                    ))}
                    {image.tags.length > 2 && (
                      <Badge variant="outline" className="text-xs">
                        +{image.tags.length - 2} more
                      </Badge>
                    )}
                  </div>
                )}
                {selectMode && (
                  <Button
                    size="sm"
                    className="w-full mt-2"
                    onClick={() => onImageSelect(image)}
                  >
                    Select Image
                  </Button>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600">
            Showing {images.length} of {pagination.total} images
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page <= 1}
              onClick={() => handlePageChange(pagination.page - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <span className="text-sm text-gray-600">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page >= pagination.totalPages}
              onClick={() => handlePageChange(pagination.page + 1)}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Image Details Dialog */}
      <Dialog open={showImageDetails} onOpenChange={setShowImageDetails}>
        <DialogContent className="max-w-2xl">
          {selectedImage && (
            <>
              <DialogHeader>
                <DialogTitle>{selectedImage.original_name}</DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <img
                    src={`${process.env.REACT_APP_API_URL}${selectedImage.file_path}`}
                    alt={selectedImage.alt_text || selectedImage.original_name}
                    className="w-full h-auto rounded-lg border"
                  />
                </div>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Details</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Size:</span>
                        <span>{formatFileSize(selectedImage.file_size)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Created:</span>
                        <span>{formatDate(selectedImage.created_at)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Creator:</span>
                        <span>{selectedImage.creator_name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Usage:</span>
                        <span>{selectedImage.usage_count} questions</span>
                      </div>
                    </div>
                  </div>
                  {selectedImage.description && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Description</h4>
                      <p className="text-sm text-gray-600">{selectedImage.description}</p>
                    </div>
                  )}
                  {selectedImage.tags && selectedImage.tags.length > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Tags</h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedImage.tags.map((tag, index) => (
                          <Badge key={index} style={{ backgroundColor: tag.color, color: 'white' }}>
                            {tag.name}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {selectMode && (
                    <Button
                      className="w-full"
                      onClick={() => {
                        onImageSelect(selectedImage);
                        setShowImageDetails(false);
                      }}
                    >
                      Select This Image
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Image Dialog */}
      <Dialog open={!!editingImage} onOpenChange={() => setEditingImage(null)}>
        <DialogContent>
          {editingImage && (
            <EditImageForm
              image={editingImage}
              onSave={(updateData) => handleUpdateImage(editingImage.id, updateData)}
              onCancel={() => setEditingImage(null)}
              availableTags={tags}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Edit Image Form Component
const EditImageForm = ({ image, onSave, onCancel, availableTags }) => {
  const [formData, setFormData] = useState({
    description: image.description || '',
    altText: image.alt_text || '',
    tags: image.tags?.map(tag => tag.name) || []
  });
  const [newTag, setNewTag] = useState('');

  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Edit Image</DialogTitle>
      </DialogHeader>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label htmlFor="edit-description">Description</Label>
          <Textarea
            id="edit-description"
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            placeholder="Brief description of the image..."
          />
        </div>
        <div>
          <Label htmlFor="edit-alt-text">Alt Text</Label>
          <Input
            id="edit-alt-text"
            value={formData.altText}
            onChange={(e) => setFormData(prev => ({ ...prev, altText: e.target.value }))}
            placeholder="Alternative text for accessibility..."
          />
        </div>
        <div>
          <Label>Tags</Label>
          <div className="flex space-x-2 mb-2">
            <Input
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              placeholder="Add a tag..."
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
            />
            <Button type="button" onClick={addTag} size="sm">
              <Tag className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {formData.tags.map((tag, index) => (
              <Badge key={index} variant="secondary" className="cursor-pointer" onClick={() => removeTag(tag)}>
                {tag} <X className="h-3 w-3 ml-1" />
              </Badge>
            ))}
          </div>
        </div>
        <div className="flex justify-end space-x-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit">
            Save Changes
          </Button>
        </div>
      </form>
    </>
  );
};

export default ImageGallery;